import React, { useState } from "react";
import useThemeHook from "../hooks/useThemeHook";
import { useTranslationHook } from "../hooks/useTranslationHook";
import { THEME_COLORS } from "../themes/DarkLight";
import { Zap, Play, CheckCircle2 } from "lucide-react";
import piNetworkAdsManager from "../utils/PiNetworkAdsManager";
import arthereumProvider from "../../backend/ArthereumProvider";
import { useDispatch, useSelector } from "react-redux";
import { actions } from "../redux";

const DirectBoostCard: React.FC = () => {
  const theme = useThemeHook();
  const getTranslationText = useTranslationHook();
  const dispatch = useDispatch();
  const userData = useSelector((state: any) => state?.actualUser);
  const user = userData?.user;

  const [loading, setLoading] = useState(false);
  const [adsWatched, setAdsWatched] = useState(0);

  const handleBoostClick = async () => {
    if (loading) return;
    setLoading(true);

    try {
      // Step 1: Watch Ad 1
      const isReady1 = await piNetworkAdsManager.isAdReady("rewarded");
      if (!isReady1) await piNetworkAdsManager.requestAd("rewarded");
      const show1 = await piNetworkAdsManager.showAd("rewarded");
      
      if (show1.result === "AD_REWARDED") {
        setAdsWatched(1);
        // Small delay between ads
        await new Promise(resolve => setTimeout(resolve, 1500));
        
        // Step 2: Watch Ad 2
        const isReady2 = await piNetworkAdsManager.isAdReady("rewarded");
        if (!isReady2) await piNetworkAdsManager.requestAd("rewarded");
        const show2 = await piNetworkAdsManager.showAd("rewarded");

        if (show2.result === "AD_REWARDED") {
          setAdsWatched(2);
          // Step 3: Activate Boost in Backend
          await arthereumProvider.activateDirectBoost(user.id);
          
          // Update Redux locally instead of re-fetching from Firestore
          dispatch(actions.setActualUser({
            ...userData,
            user: {
              ...user,
              directBoostExpiry: { seconds: Math.floor(Date.now() / 1000) + 3600 },
            },
          }));
          
          alert("BOOM! 2x Direct Boost Activated for your next claim! 🚀");
        }
      }
    } catch (error) {
      console.error("Direct Boost failed:", error);
      alert("Something went wrong. Please try again later.");
    } finally {
      setLoading(false);
      setAdsWatched(0);
    }
  };

  // Check if boost is already active
  const isBoostActive = user?.directBoostExpiry && new Date(user.directBoostExpiry.seconds * 1000) > new Date();

  if (isBoostActive) {
    return (
      <div className={`relative overflow-hidden rounded-2xl p-5 border ${
        theme === "dark" 
        ? "bg-green-900/20 border-green-500/30 text-green-400" 
        : "bg-green-50 border-green-200 text-green-600"
      }`}>
        <div className="flex items-center gap-3">
          <CheckCircle2 size={24} />
          <div>
            <h3 className="font-bold text-sm">2x Direct Boost Active!</h3>
            <p className="text-[10px] opacity-80">Your next grant claim will be doubled.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      onClick={handleBoostClick}
      className={`relative overflow-hidden rounded-2xl p-5 cursor-pointer transition-all active:scale-95 ${
        theme === "dark"
          ? "bg-gradient-to-br from-orange-900/40 to-yellow-900/40 border border-orange-500/30"
          : "bg-gradient-to-br from-orange-50 to-yellow-50 border border-orange-200"
      } ${loading ? 'opacity-70 grayscale' : ''}`}
    >
      <div className="flex items-center justify-between relative z-10">
        <div className="flex items-center gap-4">
          <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-orange-100 text-orange-600 shadow-sm relative">
            <Zap size={24} fill={loading ? "none" : "currentColor"} className={loading ? 'animate-pulse' : ''} />
            {loading && adsWatched > 0 && (
              <div className="absolute -top-1 -right-1 bg-orange-600 text-white text-[8px] rounded-full w-4 h-4 flex items-center justify-center font-bold">
                {adsWatched}/2
              </div>
            )}
          </div>
          
          <div>
            <h3 className={`font-bold text-base ${theme === "dark" ? "text-white" : "text-gray-900"}`}>
              {loading ? `Watching Ads... (${adsWatched}/2)` : "Direct 2x Boost ⚡"}
            </h3>
            <p className={`text-xs ${theme === "dark" ? "text-gray-400" : "text-gray-600"}`}>
              Watch 2 ads to double your next grant!
            </p>
          </div>
        </div>

        <div className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-orange-600 text-white text-xs font-bold shadow-lg shadow-orange-500/20">
          <Play size={12} fill="currentColor" />
          <span>BOOST</span>
        </div>
      </div>
    </div>
  );
};

export default DirectBoostCard;
